import {
    Col,
    Popconfirm,
    Modal,
    Input,
    Button,
    Table,
    Row,
    Form,
    Switch,
    Tooltip,
    Popover,
    Select,
    Tag,
    Spin,
    Checkbox,
    DatePicker,
} from "antd";
import {
    IAccess,
    IClientAccess,
    IUserRecord,
    CustomOptionType,
    Platforms,
    IClient,
    IMainContext,
    TUsersFormInitialState,
    TUsersFormActions,
    TChannels,
    ClientGroup,
    IUserPrivilegesState,
    IClientAccessAddition,
    IClientAccessModification,
    IClientChanges,
    ParentModule,
} from "@/ts/common-types";
import { showToast } from "@/ts/modals";
import {
    addUser,
    deleteUser,
    sendPasswordResetMail,
    updateUserDetails,
    fetchCountriesData,
    fetchTimezones,
    titleCase,
    convertIsoDate,
    getDecryptedUserDetails,
    fetchChannelsForClientIds,
    groupClientsOnAccessConfig,
    reactivateUser,
} from "@/ts/utils";
import {
    useCallback,
    useEffect,
    useMemo,
    useState,
    useReducer,
    useContext,
    useRef,
} from "react";
import { useSelector } from "react-redux";
import {
    CheckOutlined,
    CloseOutlined,
    InfoCircleOutlined,
} from "@ant-design/icons";
import { IoIosCloseCircleOutline } from "react-icons/io";
import { GrEdit } from "react-icons/gr";
import {
    getAllChannelsForClientIds,
    getAllClients,
    getAllUsers,
    getSavedRolePermissions,
    getUserRoles,
} from "@/ts/retrieve-data";
import { pluck, debounce, isEqual } from "underscore";
import { isValidPhoneNumber } from "libphonenumber-js";
import PhoneInput from "react-phone-input-2";
import * as Sentry from "@sentry/react";
import UserRolesComponent from "./Roles";
import {
    DEFAULT_PAGE_SIZE_OPTIONS,
    mainContext,
    USERS_FORM_INITIAL_STATE,
    usersFormRules,
} from "@/ts/constants";
import { usePermissionCheck, useUserTimezone } from "@/hooks/AllCustomHooks";
import { AiOutlineDelete } from "react-icons/ai";
import { VscDebugRestart } from "react-icons/vsc";
import { GrClone } from "react-icons/gr";
import "@/scss/_channel-icons.scss";
import ChannelSelect from "@/components/drop-downs/ChannelSelect";
import dayjs from "dayjs";
import { ExpirationDateCell } from "./components/ExpirationDateCell";
import ResetPasswordModal from "@/components/modals/admin-settings/ResetPasswordModal";
import { useNavigate } from "react-router-dom";

const { Option } = Select;

const DEFAULT_ROLE_ID = 5; //Read Only

const UserComponent = () => {
    const { cerbosPermissions } = useContext<IMainContext>(mainContext);
    const [form] = Form.useForm();
    const [addUserDisplay, setAddUserDisplay] = useState(false);
    const [privilegesModalDisp, setPrivilegesModalDisp] = useState(false);
    const [isActive, setIsActive] = useState(true);
    const [editUserData, setEditUserData] = useState(null);
    const [resetPasswordLoading, setResetPasswordLoading] = useState<
        string | null
    >(null);
    const [resetPasswordModalVisible, setResetPasswordModalVisible] =
        useState(false);
    const [selectedUserForReset, setSelectedUserForReset] =
        useState<IUserRecord | null>(null);
    const [paginationData, setPaginationData] = useState({
        usersList: new Map<number, any>(),
        currentPage: 1,
        pageSize: 10,
        totalUsers: 0,
        loading: false,
    });

    const [countriesData, setCountriesData] = useState([]);
    const [timezones, setTimezones] = useState([]);
    const [availableRoles, setAvailableRoles] = useState([]);
    const [channels, setChannels] = useState([]);
    const userClients = useSelector(
        (state: { clients: { value: IClient[] } }) => state.clients.value
    );
    const currentUserData = useSelector(
        (state: { userData: { value: any } }) => state.userData.value
    );
    const [clients, setClients] = useState([]);
    const [cloneUserDisplay, setCloneUserDisplay] = useState(false);
    const navigate = useNavigate();

    const currentTimeZone = useUserTimezone();

    const [userPrivileges, setUserPrivileges] = useState<IUserPrivilegesState>(
        {}
    );
    const [selectedClientId, setSelectedClientId] = useState<number | null>(
        null
    );

    const [selectedRolesMap, setSelectedRolesMap] = useState<{
        [clientId: number]: number;
    }>({});

    const [searchQuery, setSearchQuery] = useState("");
    const [savedPermissionData, setSavedPermissionData] = useState([]);

    // request tracking ref
    const currentRequestId = useRef<number>(0);

    const usersFormReducer = (
        state: TUsersFormInitialState,
        action: TUsersFormActions
    ) => {
        switch (action.type) {
            case "SET_CHANNELS_LOADING":
                return { ...state, channelsLoading: action.payload };
            case "SET_FORM_LOADING":
                return { ...state, formLoading: action.payload };
            case "SET_REACTIVATE_LOADING":
                return { ...state, reactivateUserLoading: action.payload };
            case "SET_FORM_SUBMISSION_LOADING":
                return { ...state, formSubmissionLoading: action.payload };
            default:
                throw new Error(
                    `Unhandled action type: ${(action as any).type}`
                );
        }
    };

    const [formState, setFormState] = useReducer(
        usersFormReducer,
        USERS_FORM_INITIAL_STATE
    );

    const {
        channelsLoading,
        formLoading,
        reactivateUserLoading,
        formSubmissionLoading,
    } = formState;

    const {
        canAddUser,
        canResetPassword,
        canDeleteUser,
        canListPermissions,
        canShowAllClients,
        canReactivateUser,
    } = usePermissionCheck();

    useEffect(() => {
        const getClients = async () => {
            try {
                setFormState({ type: "SET_FORM_LOADING", payload: true });
                if (canShowAllClients) {
                    const allClients = await getAllClients();
                    setClients(allClients);
                } else {
                    setClients(userClients);
                }
                setFormState({ type: "SET_FORM_LOADING", payload: false });
            } catch (err) {
                setFormState({ type: "SET_FORM_LOADING", payload: false });
                showToast("danger", "Unable to get clients !!");
            }
        };
        getClients();
    }, [cerbosPermissions, addUserDisplay, editUserData]);

    const roleIdToNameMap = useMemo(() => {
        const map = new Map<number, string>();
        availableRoles?.forEach((role) => {
            map.set(role.role_id, role.role_name);
        });
        return map;
    }, [availableRoles]);

    const getRoleName = (roleId: number): string => {
        return roleIdToNameMap.get(roleId) || `Role ${roleId}`;
    };

    const debouncedGetChannels = debounce(
        async (value: number[], callback: (channels: TChannels) => void) => {
            const allClientChannels = await getAllChannelsForClientIds(value);
            callback(allClientChannels);
        },
        200
    );

    const handleClientChange = async (value: number[], index: number) => {
        try {
            const access = form.getFieldValue("access");

            // Ensure the `clients` array is extended to accommodate the index
            while (access.length <= index) {
                access.push({
                    clients: [],
                    platforms: [],
                    role_id: DEFAULT_ROLE_ID,
                    privileges: [],
                    channels: [],
                });
            }

            access[index].clients = value;

            if (access[index].clients.length > 0) {
                setFormState({ type: "SET_CHANNELS_LOADING", payload: index });

                debouncedGetChannels(value, (getChannels) => {
                    setChannels((prevChannels) => {
                        const updatedChannels = [...prevChannels];

                        // Ensure array extension
                        while (updatedChannels.length <= index) {
                            updatedChannels.push([]);
                        }

                        updatedChannels[index] = getChannels;
                        return updatedChannels;
                    });

                    setFormState({
                        type: "SET_CHANNELS_LOADING",
                        payload: null,
                    });
                });
            } else {
                // Clear channels for the given index
                setChannels((prevChannels) => {
                    const updatedChannels = [...prevChannels];

                    while (updatedChannels.length <= index) {
                        updatedChannels.push([]);
                    }

                    updatedChannels[index] = []; // Clear the channels at the index

                    return updatedChannels;
                });
                access[index].channels = [];
            }
            // Update form state after channels update
            form.setFieldsValue({ access: access });
        } catch (err) {
            setFormState({ type: "SET_CHANNELS_LOADING", payload: null });
        }
    };

    const handleRoleChange = (value: number, index: number) => {
        const clients = form.getFieldValue("access");

        while (clients.length <= index) {
            clients.push({
                client: [],
                platforms: [],
                role: DEFAULT_ROLE_ID,
                privileges: [],
            });
        }

        const rolePerms: number[] = value
            ? savedPermissionData
                  .filter((p) => value === p.role_id)
                  .map((p) => p.permission_id)
            : [];
        clients[index].privileges = rolePerms;
        clients[index].role_id = value;
        form.setFieldsValue({ access: clients });
    };

    const handleChannelChange = (channelId: Array<number>, index: number) => {
        const clients = form.getFieldValue("clients");
        const access = form.getFieldValue("access");

        if (!clients && access) {
            // if it is user editing state
            access[index].channels = channelId;
            form.setFieldsValue({ access });
        } else {
            // if it is add user state
            if (!clients[index].channels) {
                clients[index].channels = [];
            }

            clients[index].channels = Array.from(
                new Set([...clients[index].channels, ...channelId])
            );
            form.setFieldsValue({ clients });
        }
    };

    const handleUserFieldToggle = (
        field: "status" | "is_super_admin",
        checked: boolean
    ) => {
        form.setFieldsValue({ [field]: checked });
        if (!editUserData && field === "status") {
            setIsActive(checked);
        }
    };

    const handleWelcomeEmailChange = (checked: boolean) => {
        form.setFieldsValue({
            sendWelcomeEmail: checked,
        });
    };

    useEffect(() => {
        fetchCountriesData()
            .then((countries) => {
                setCountriesData(countries);
            })
            .catch((err: any) => {
                Sentry.captureException(err);
            });

        fetchTimezones()
            .then((data) => {
                setTimezones(data);
            })
            .catch((err) => {
                Sentry.captureException(err);
            });
        getUserRoles().then((data: any) => {
            setAvailableRoles(data);
        });
        getSavedRolePermissions().then((data: any) => {
            setSavedPermissionData(data);
        });
    }, [addUserDisplay, editUserData]);

    const updatePaginationAndSearch = (value: string) => {
        setPaginationData((prev) => ({
            ...prev,
            currentPage: 1,
            usersList: new Map(),
            loading: true,
        }));
        setSearchQuery(value);
    };

    const debouncedSearch = debounce((value: string) => {
        if (!value || value.length > 3) {
            updatePaginationAndSearch(value);
        }
    }, 500);

    useEffect(() => {
        return () => {
            debouncedSearch.cancel();
            debouncedGetChannels.cancel();
        };
    }, []);

    const platformOptions = [
        Platforms.ACTIVATION,
        Platforms.PLANNING,
        Platforms.MEASUREMENT,
        Platforms.CHATBOT,
    ].map((item: string) => titleCase(item));

    const rowSelection = {
        getCheckboxProps: (record: any) => ({
            name: record.name,
        }),
    };

    const handleCloneUser = async (record: IUserRecord) => {
        try {
            setAddUserDisplay(true);
            setCloneUserDisplay(true);

            setFormState({ type: "SET_FORM_LOADING", payload: true });

            // Group clients based on their access configuration
            const clientGroups: ClientGroup[] =
                groupClientsOnAccessConfig(record);

            const allChannels = await fetchChannelsForClientIds(
                record,
                clientGroups
            );

            setChannels(allChannels);

            form.setFieldsValue({
                firstName: "",
                lastName: "",
                email: "",
                phone: "",
                country: record.country || "",
                timezone: record.timezone || "",
                newsletterSubscription: [],
                expiration: null,
                access: clientGroups,
                status: true,
                sendWelcomeEmail: true,
            });
            setFormState({ type: "SET_FORM_LOADING", payload: false });
        } catch (err) {
            setFormState({ type: "SET_FORM_LOADING", payload: false });
            showToast("danger", "Error fetching user details");
        }
    };

    const handleCancelClone = () => {
        form.setFieldsValue({
            firstName: "",
            lastName: "",
            email: "",
            phone: "",
            country: "",
            timezone: "",
            newsletterSubscription: [],
            expiration: null,
            access: [
                {
                    clients: [],
                    platforms: [],
                    role_id: DEFAULT_ROLE_ID,
                    privileges: [],
                    channels: [],
                },
            ],
            status: true,
            sendWelcomeEmail: true,
        });
        setCloneUserDisplay(false);
    };

    const handleResetPassword = async () => {
        if (!selectedUserForReset) return;

        try {
            setResetPasswordLoading(selectedUserForReset.uid);
            await sendPasswordResetMail({
                userId: selectedUserForReset.uid,
            });
            showToast(
                "success",
                `Password reset email sent to ${selectedUserForReset.email}`
            );
        } catch (error) {
            showToast("danger", "Failed to send password reset email");
        } finally {
            setResetPasswordLoading(null);
            setResetPasswordModalVisible(false);
            setSelectedUserForReset(null);
        }
    };

    const columns = [
        {
            title: "Name",
            dataIndex: "name",
            key: "name",
            render: (text: string, record: IUserRecord) => (
                <>
                    {record.first_name || ""} {record.last_name || ""}
                    <br />
                    <span style={{ color: "gray" }}>{record.email}</span>
                </>
            ),
        },
        {
            title: "Client",
            dataIndex: "client",
            key: "client",
            render: (text: string, record: IUserRecord) => {
                const clientString = record.access.length
                    ? pluck(record.access, "clients")
                          .flat()
                          .map((client: IClientAccess) => client.client_name)
                          .join(", ")
                    : "";
                return (
                    <Popover
                        placement="right"
                        title={"Client Info"}
                        content={<p>{clientString}</p>}
                        styles={{
                            body: {
                                width: "200px",
                                padding: "20px",
                                height: "250px",
                                overflowY: "auto",
                                overflowX: "hidden",
                            },
                        }}
                    >
                        <p>
                            {clientString.substring(0, 50) +
                                (clientString.length > 50 ? "..." : "")}
                        </p>
                    </Popover>
                );
            },
        },
        {
            title: "Platforms",
            dataIndex: "platforms",
            key: "platforms",
            render: (text: string, record: IUserRecord) => {
                const uniquePlatforms: string[] = Array.from(
                    new Set(
                        record.access.flatMap((item) => item.platforms) || []
                    )
                );

                return (
                    <Popover
                        placement="right"
                        title="Platform Access"
                        content={
                            <div
                                style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: "4px",
                                }}
                            >
                                {uniquePlatforms.map(
                                    (platform: string, index: number) => (
                                        <Tag
                                            key={`${platform}-${index}`}
                                            color="blue"
                                        >
                                            {platform}
                                        </Tag>
                                    )
                                )}
                            </div>
                        }
                        styles={{
                            body: {
                                width: "200px",
                                padding: "20px",
                                maxHeight: "250px",
                                overflowY: "auto",
                                overflowX: "hidden",
                            },
                        }}
                    >
                        <p>
                            {uniquePlatforms.join(", ").length > 50
                                ? uniquePlatforms.join(", ").substring(0, 50) +
                                  "..."
                                : uniquePlatforms.join(", ")}
                        </p>
                    </Popover>
                );
            },
        },
        {
            title: "Role",
            dataIndex: "role_id",
            key: "role_id",
            render: (text: string, record: IUserRecord) => {
                const roles: string[] = Array.from(
                    new Set(
                        record.access.flatMap((access: IAccess) =>
                            getRoleName(access.role_id)
                        ) || []
                    )
                );

                // Add Super Admin if user has is_super_admin flag
                if (record.is_super_admin) {
                    roles.unshift("Super Admin");
                }

                const roleString = roles.join(", ");

                return (
                    <Popover
                        placement="right"
                        title="User Roles"
                        content={
                            <div
                                style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: "4px",
                                }}
                            >
                                {roles.map(
                                    (roleName: string, index: number) => (
                                        <Tag
                                            key={`${roleName}-${index}`}
                                            color={
                                                roleName === "Super Admin"
                                                    ? "blue"
                                                    : "purple"
                                            }
                                            style={
                                                roleName === "Super Admin"
                                                    ? {
                                                          background:
                                                              "linear-gradient(45deg, #e6f4ff, #bae0ff)",
                                                          border: "1px solid #91caff",
                                                          fontWeight: "bold",
                                                          textShadow:
                                                              "0 1px 2px rgba(0,0,0,0.1)",
                                                      }
                                                    : {}
                                            }
                                        >
                                            {roleName}
                                        </Tag>
                                    )
                                )}
                            </div>
                        }
                        styles={{
                            body: {
                                width: "200px",
                                padding: "20px",
                                maxHeight: "250px",
                                overflowY: "auto",
                                overflowX: "hidden",
                            },
                        }}
                    >
                        <div
                            className={
                                record.is_super_admin
                                    ? "bg-info-subtle border border-info rounded p-2"
                                    : ""
                            }
                        >
                            <p style={{ margin: 0 }}>
                                {roleString.length > 50
                                    ? roleString.substring(0, 50) + "..."
                                    : roleString}
                            </p>
                        </div>
                    </Popover>
                );
            },
        },
        {
            title: "Last Login",
            dataIndex: "lastLogin",
            key: "lastLogin",
            render: (text: string, record: IUserRecord) => (
                <div>
                    <div>
                        {record.last_login
                            ? `${convertIsoDate(
                                  record.last_login,
                                  currentTimeZone
                              )}`
                            : "Never"}
                    </div>
                    <div className="small text-muted">
                        {record.last_login
                            ? `${convertIsoDate(record.last_login)} - UTC`
                            : ""}
                    </div>
                </div>
            ),
        },
        {
            title: "Expiration",
            dataIndex: "expiry_date",
            key: "expiry_date",
            render: (text: string, record: IUserRecord) => (
                <ExpirationDateCell
                    record={record}
                    currentTimeZone={currentTimeZone}
                />
            ),
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            render: (status: string, record: IUserRecord) => (
                <Row>
                    <Col span={12}>
                        <Tag
                            bordered={false}
                            color={record.status ? "green" : "red"}
                        >
                            <strong>
                                {record.is_archived
                                    ? "Archived"
                                    : record.status
                                    ? "Active"
                                    : "InActive"}
                            </strong>
                        </Tag>
                    </Col>
                    <Col span={12}>
                        <div className="d-flex align-items-center gap-2">
                            <Tooltip
                                title={
                                    record.is_archived
                                        ? "Cannot edit archived user"
                                        : record.is_super_admin &&
                                          !currentUserData?.is_super_admin
                                        ? "Editing super admin not allowed"
                                        : "Edit User"
                                }
                            >
                                <Button
                                    type="text"
                                    className="p-1 h-auto"
                                    icon={
                                        <GrEdit className="fs-5 cursor-pointer" />
                                    }
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setEditUserData(record);
                                        setAddUserDisplay(true);
                                    }}
                                    disabled={
                                        record.is_archived ||
                                        (record.is_super_admin &&
                                            !currentUserData?.is_super_admin)
                                    }
                                />
                            </Tooltip>
                            <Tooltip
                                title={
                                    !canResetPassword
                                        ? "Reset password not allowed"
                                        : record.is_archived
                                        ? "Cannot reset password for archived user"
                                        : record.is_super_admin &&
                                          !currentUserData?.is_super_admin
                                        ? "Resetting super admin password not allowed"
                                        : "Reset Password"
                                }
                            >
                                <Button
                                    type="text"
                                    className="p-1 h-auto"
                                    icon={
                                        <VscDebugRestart className="fs-5 cursor-pointer" />
                                    }
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedUserForReset(record);
                                        setResetPasswordModalVisible(true);
                                    }}
                                    disabled={
                                        !canResetPassword ||
                                        record.is_archived ||
                                        (record.is_super_admin &&
                                            !currentUserData?.is_super_admin)
                                    }
                                    loading={
                                        resetPasswordLoading === record.uid
                                    }
                                />
                            </Tooltip>

                            <Tooltip
                                title={
                                    !canDeleteUser
                                        ? "Delete user not allowed"
                                        : record.is_archived
                                        ? "Cannot delete archived user"
                                        : record.is_super_admin &&
                                          !currentUserData?.is_super_admin
                                        ? "Deleting super admin not allowed"
                                        : "Delete User"
                                }
                            >
                                <Popconfirm
                                    title="Are you sure you want to delete this user?"
                                    onConfirm={async () => {
                                        const response = await deleteUser({
                                            uid: record.uid,
                                        });

                                        if (response?.status) {
                                            setPaginationData((prev) => ({
                                                ...prev,
                                                usersList: new Map<
                                                    number,
                                                    any
                                                >(),
                                                currentPage: 1,
                                                loading: true,
                                            }));
                                        }
                                    }}
                                    okText="Yes"
                                    cancelText="No"
                                    styles={{
                                        body: {
                                            padding: "10px",
                                        },
                                    }}
                                >
                                    <Button
                                        type="text"
                                        danger
                                        className="p-1 h-auto"
                                        icon={
                                            <AiOutlineDelete className="fs-5 cursor-pointer" />
                                        }
                                        onClick={(e) => {
                                            e.stopPropagation();
                                        }}
                                        disabled={
                                            !canDeleteUser ||
                                            record.is_archived ||
                                            (record.is_super_admin &&
                                                !currentUserData?.is_super_admin)
                                        }
                                    />
                                </Popconfirm>
                            </Tooltip>
                            <Tooltip
                                title={
                                    record.is_archived
                                        ? "Cannot clone archived user"
                                        : record.is_super_admin &&
                                          !currentUserData?.is_super_admin
                                        ? "Cloning super admin not allowed"
                                        : "Clone User"
                                }
                            >
                                <Button
                                    type="text"
                                    className="p-1 h-auto"
                                    icon={
                                        <GrClone className="fs-5 cursor-pointer" />
                                    }
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleCloneUser(record);
                                    }}
                                    disabled={
                                        record.is_archived ||
                                        (record.is_super_admin &&
                                            !currentUserData?.is_super_admin)
                                    }
                                />
                            </Tooltip>
                        </div>
                    </Col>
                </Row>
            ),
        },
    ];

    const fetchUsers = useCallback(
        async (page: number, pageSize: number) => {
            const thisRequestId = ++currentRequestId.current;

            setPaginationData((prev) => ({
                ...prev,
                loading: true,
            }));

            if (paginationData.usersList.has(page)) {
                setPaginationData((prev) => ({
                    ...prev,
                    currentPage: page,
                    loading: false,
                }));
                return;
            }

            try {
                const res = await getAllUsers({
                    page,
                    pageSize,
                    searchQuery: searchQuery,
                });

                // If this isn't the latest request, ignore it
                if (thisRequestId !== currentRequestId.current) {
                    return;
                }

                const usersData = res?.usersData;
                const totalUsers = res?.totalUsers;

                if (!usersData) {
                    throw new Error("Invalid response structure");
                }

                setPaginationData((prev) => ({
                    ...prev,
                    usersList: new Map(prev.usersList).set(page, usersData),
                    currentPage: page,
                    totalUsers: totalUsers,
                    loading: false,
                }));
            } catch (error) {
                // Only update state if this was the latest request
                if (thisRequestId === currentRequestId.current) {
                    setPaginationData((prev) => ({
                        ...prev,
                        loading: false,
                    }));
                }
            }
        },
        [paginationData.usersList, searchQuery]
    );

    useEffect(() => {
        fetchUsers(1, paginationData.pageSize);
    }, [searchQuery, fetchUsers, paginationData.pageSize]);

    useEffect(() => {
        if (paginationData.currentPage > 1) {
            fetchUsers(paginationData.currentPage, paginationData.pageSize);
        }
    }, [paginationData.currentPage, fetchUsers, paginationData.pageSize]);

    useEffect(() => {
        const setEditForm = async () => {
            if (!form) return;

            if (editUserData) {
                try {
                    setFormState({ type: "SET_FORM_LOADING", payload: true });

                    // Group clients based on their access configuration
                    const clientGroups: ClientGroup[] =
                        groupClientsOnAccessConfig(editUserData);

                    // Now fetchChannelsForClientIds is async
                    const allChannels = await fetchChannelsForClientIds(
                        editUserData,
                        clientGroups
                    );
                    setChannels(allChannels);

                    form.setFieldsValue({
                        firstName: editUserData?.first_name || "",
                        lastName: editUserData?.last_name || "",
                        email: editUserData?.email || "",
                        phone: editUserData?.phone || "",
                        country: editUserData?.country || null,
                        timezone: editUserData?.timezone || null,
                        newsletterSubscription: [],
                        expiration: editUserData?.expiry_date
                            ? dayjs(editUserData.expiry_date)
                            : null,
                        access: clientGroups,
                        status: editUserData?.status ?? true,
                        is_super_admin: editUserData?.is_super_admin ?? false,
                    });
                    setFormState({ type: "SET_FORM_LOADING", payload: false });
                } catch (err) {
                    setFormState({ type: "SET_FORM_LOADING", payload: false });
                    showToast("danger", "Error fetching user details");
                }
            } else {
                form.resetFields();
            }
        };
        setEditForm();
    }, [editUserData, form]);

    useEffect(() => {
        if (userPrivileges && selectedClientId) {
            const currentClients = form.getFieldValue("access");
            const updatedClients = currentClients.map((client: any) => {
                if (
                    client.clients &&
                    client.clients.toString() === selectedClientId.toString()
                ) {
                    const updatedClient = {
                        ...client,
                        platforms: client.platforms,
                        privileges: (client.privileges =
                            userPrivileges[selectedClientId] || []), // Add privileges to client
                    };
                    return updatedClient;
                }
                return client;
            });

            form.setFieldsValue({
                clients: updatedClients,
            });
        }
    }, [userPrivileges, selectedClientId, form]);
    
    const PrivilegesModal = () => {
        return (
            <Modal
                title="Set Privileges"
                open={addUserDisplay && privilegesModalDisp}
                onOk={() => setPrivilegesModalDisp(false)}
                onCancel={() => setPrivilegesModalDisp(false)}
                width={900}
                footer={null}
                styles={{ body: { paddingBottom: "20px" } }}
            >
                <UserRolesComponent
                    privilegesModalDisp={privilegesModalDisp}
                    setPrivilegesModalDisp={setPrivilegesModalDisp}
                    clientId={selectedClientId}
                    selectedRole={selectedRolesMap[selectedClientId] || 0}
                    userPrivileges={userPrivileges}
                    setUserPrivileges={setUserPrivileges}
                    form={form}
                />
            </Modal>
        );
    };

    const ResetPasswordConfirmationModal = () => {
        return (
            <ResetPasswordModal
                visible={resetPasswordModalVisible}
                selectedUser={selectedUserForReset}
                loading={resetPasswordLoading === selectedUserForReset?.uid}
                onConfirm={handleResetPassword}
                onCancel={() => {
                    setResetPasswordModalVisible(false);
                    setSelectedUserForReset(null);
                }}
            />
        );
    };

    const isArchived = editUserData?.is_archived;

    const formInitialValues: any = {
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        country: "",
        timezone: "",
        newsletterSubscription: [],
        expiration: null,
        access: [
            {
                clients: [],
                platforms: [],
                role_id: DEFAULT_ROLE_ID,
                privileges: [],
                channels: [],
            },
        ],
        status: true,
        sendWelcomeEmail: true,
    };

    const filterOption = (input: string, option: CustomOptionType) =>
        option.children.toString().toLowerCase().includes(input.toLowerCase());

    const checkIsClientOptionDisabled = (
        clientIndex: number,
        client: IClient
    ) => {
        // Get all selected clients from other groups
        const otherGroupsClients = form
            .getFieldValue("access")
            ?.reduce((acc: any, group: IAccess, i: number) => {
                if (i !== clientIndex) {
                    return [...acc, ...(group.clients || [])];
                }
                return acc;
            }, []);

        // Option should be disabled if it's selected in another group
        const isDisabled = otherGroupsClients?.includes(client.id);
        return isDisabled;
    };

    const handleFormSubmit = async (values: any) => {
        try {
            setFormState({
                type: "SET_FORM_SUBMISSION_LOADING",
                payload: true,
            });

            const userData: {
                userId: string;
                firstName: string;
                lastName: string;
                email: string;
                country: string;
                timezone: string;
                status: boolean;
                is_super_admin: boolean;
                phone?: string;
                expiry_date?: string;
                default_client?: number;
                clientChanges?: IClientChanges;
            } = {
                userId: editUserData?.user_id,
                firstName: values.firstName,
                lastName: values.lastName,
                email: values.email,
                country: values.country,
                timezone: values.timezone,
                status: !!values.status,
                is_super_admin: values.is_super_admin,
                ...(values.phone &&
                    values.phone !== "" && { phone: values.phone }),
                ...(values.expiration && {
                    expiry_date: values.expiration.format("YYYY-MM-DD"),
                }),
            };

            // Initialize clientChanges
            const clientChanges: IClientChanges = {
                additions: [] as IClientAccessAddition[],
                deletions: [] as number[],
                modifications: [] as IClientAccessModification[],
            };

            if (editUserData || cloneUserDisplay) {
                if (editUserData) {
                    // Get all existing client IDs from the user's access
                    const existingClientIds = new Set<number>();
                    const existingClientAccessMap = new Map<number, IAccess>();

                    editUserData.access.forEach((access: IAccess) => {
                        access.clients.forEach((client: IClientAccess) => {
                            existingClientIds.add(client.client_id);
                            existingClientAccessMap.set(client.client_id, {
                                ...access,
                                clients: [client],
                            });
                        });
                    });

                    // Get all current client IDs from the form
                    const currentClientIds = new Set<number>();
                    const currentClientAccessMap = new Map<number, any>();

                    values.access.forEach((access: any) => {
                        access.clients.forEach((clientId: number) => {
                            currentClientIds.add(clientId);
                            currentClientAccessMap.set(clientId, {
                                platforms: access.platforms,
                                role_id: access.role_id,
                                privileges: access.privileges
                                    ? access.privileges.map(
                                          (privilege: string) => +privilege
                                      )
                                    : [],
                                channels: access.channels || [],
                            });
                        });
                    });

                    // Find clients that are being added (in current but not in existing)
                    values.access.forEach((access: any) => {
                        const newClientsInThisGroup = access.clients.filter(
                            (clientId: number) =>
                                !existingClientIds.has(clientId)
                        );

                        if (newClientsInThisGroup.length > 0) {
                            clientChanges.additions.push({
                                clients: newClientsInThisGroup,
                                platforms: access.platforms,
                                role_id: access.role_id,
                                privileges: access.privileges
                                    ? access.privileges.map(
                                          (privilege: string) => +privilege
                                      )
                                    : [],
                                channels: access.channels || [],
                            });
                        }
                    });

                    // Find clients that are being removed (in existing but not in current)
                    existingClientIds.forEach((clientId) => {
                        if (!currentClientIds.has(clientId)) {
                            clientChanges.deletions.push(clientId);
                        }
                    });

                    // Find clients that are being modified (in both but with different details)
                    existingClientIds.forEach((clientId) => {
                        if (currentClientIds.has(clientId)) {
                            const existingAccess =
                                existingClientAccessMap.get(clientId);
                            const currentAccess =
                                currentClientAccessMap.get(clientId);

                            // Normalize the data for comparison
                            const normalizedExistingPrivileges = (
                                existingAccess?.privileges || []
                            )
                                .map((priv: string | number) => Number(priv))
                                .sort((a: number, b: number) => a - b);

                            const normalizedCurrentPrivileges = (
                                currentAccess.privileges || []
                            )
                                .map((priv: string | number) => Number(priv))
                                .sort((a: number, b: number) => a - b);

                            const existingChannelIds = (
                                existingAccess?.channels || []
                            )
                                .map(
                                    (ch: { channel_id: number }) =>
                                        ch.channel_id
                                )
                                .sort((a: number, b: number) => a - b);

                            const currentChannelIds = (
                                currentAccess.channels || []
                            ).sort((a: number, b: number) => a - b);

                            // Compare platforms, role, privileges and channels
                            const hasChanges =
                                !isEqual(
                                    (existingAccess?.platforms || []).sort(),
                                    (currentAccess.platforms || []).sort()
                                ) ||
                                existingAccess?.role_id !==
                                    currentAccess.role_id ||
                                !isEqual(
                                    normalizedExistingPrivileges,
                                    normalizedCurrentPrivileges
                                ) ||
                                !isEqual(existingChannelIds, currentChannelIds);

                            if (hasChanges) {
                                clientChanges.modifications.push({
                                    client_id: clientId,
                                    platforms: currentAccess.platforms,
                                    role_id: currentAccess.role_id,
                                    privileges: currentAccess.privileges,
                                    channels: currentAccess.channels,
                                });
                            }
                        }
                    });

                    // Check if default client is being removed
                    const isDefaultClientRemoved =
                        editUserData.default_client &&
                        clientChanges.deletions.includes(
                            editUserData.default_client
                        );

                    if (
                        isDefaultClientRemoved ||
                        !editUserData.default_client
                    ) {
                        if (
                            values.access.length > 0 &&
                            values.access[0].clients.length > 0
                        ) {
                            userData.default_client =
                                values.access[0].clients[0];
                        } else if (currentClientIds.size > 0) {
                            userData.default_client =
                                Array.from(currentClientIds)[0];
                        } else {
                            userData.default_client = null;
                        }
                    } else {
                        userData.default_client = editUserData.default_client;
                    }

                    userData.clientChanges = clientChanges;
                } else {
                    // For cloning process all clients as additions
                    values.access.forEach((client: any) => {
                        clientChanges.additions.push({
                            clients: client.clients,
                            platforms: client.platforms,
                            role_id: client.role_id,
                            privileges: client.privileges
                                ? client.privileges.map(
                                      (privilege: string) => +privilege
                                  )
                                : [],
                            channels: client.channels || [],
                        });
                    });
                }
            }

            const response = editUserData
                ? await updateUserDetails(userData)
                : await addUser({
                      ...userData,
                      access: values.access, // Keep access array only for new user
                      sendWelcomeEmail: values.sendWelcomeEmail,
                      default_client:
                          values.access.length > 0 &&
                          values.access[0].clients.length > 0
                              ? values.access[0].clients[0]
                              : null,
                  });

            if (response.status) {
                setAddUserDisplay(false);
                setPaginationData((prev) => ({
                    ...prev,
                    usersList: new Map<number, any>(),
                }));

                if (!editUserData) {
                    form.setFieldsValue(formInitialValues);
                } else {
                    setEditUserData(null);
                }
            }
            setFormState({
                type: "SET_FORM_SUBMISSION_LOADING",
                payload: false,
            });
        } catch (error) {
            showToast("danger", "Error updating user details");
            setFormState({
                type: "SET_FORM_SUBMISSION_LOADING",
                payload: false,
            });
        }
    };

    const handleReactiveUser = async (uid: string) => {
        try {
            setFormState({ type: "SET_REACTIVATE_LOADING", payload: true });
            const response = await reactivateUser(uid);

            if (response.status) {
                setFormState({
                    type: "SET_REACTIVATE_LOADING",
                    payload: false,
                });
                showToast(
                    `${response.status ? "success" : "danger"}`,
                    response.message
                );
                setAddUserDisplay(false);
                setPaginationData((prev) => ({
                    ...prev,
                    usersList: new Map<number, any>(),
                }));

                if (!editUserData) {
                    form.setFieldsValue(formInitialValues);
                } else {
                    setEditUserData(null);
                }
            }
        } catch (err) {
            setFormState({ type: "SET_REACTIVATE_LOADING", payload: false });
            showToast("danger", "Error re-activating user");
        }
    };

    const handleView = (record: any) => {
        navigate(
            `/${ParentModule.ADMIN_SETTINGS}/settings/users/${record.user_id}`
        );
    };

    const handlePaste =
        (clientIndex: number) => (e: React.ClipboardEvent<HTMLDivElement>) => {
            e.preventDefault();

            const pastedText = e.clipboardData.getData("text");

            const pastedIds = pastedText
                .split(",")
                .map((id) => id.trim())
                .filter((id) => id)
                .map((id) => parseInt(id, 10))
                .filter((id) => !isNaN(id));

            const currentAccess = form.getFieldValue("access") || [];

            const otherGroupsClients = currentAccess.reduce(
                (acc: number[], group: IAccess, i: number) => {
                    if (i !== clientIndex) {
                        return [...acc, ...(group.clients || [])];
                    }
                    return acc;
                },
                []
            );

            const currentGroupClients =
                currentAccess[clientIndex]?.clients || [];

            const validClientIds = pastedIds.filter((id) => {
                const isValidClient = clients.some(
                    (client) => client.id === id
                );
                const isAlreadyAssignedElsewhere =
                    otherGroupsClients.includes(id);
                return isValidClient && !isAlreadyAssignedElsewhere;
            });

            if (validClientIds.length > 0) {
                const mergedClients = Array.from(
                    new Set([...currentGroupClients, ...validClientIds])
                );

                while (currentAccess.length <= clientIndex) {
                    currentAccess.push({
                        clients: [],
                        platforms: [],
                        role_id: DEFAULT_ROLE_ID,
                        privileges: [],
                        channels: [],
                    });
                }

                // Update the clients field with merged selection
                currentAccess[clientIndex].clients = mergedClients;

                form.setFieldsValue({ access: currentAccess });

                handleClientChange(mergedClients, clientIndex);

                showToast("success", "Valid client IDs successfully added");
            }

            const invalidIds = pastedIds.filter(
                (id) => !clients.some((client) => client.id === id)
            );
            const alreadyAssignedIds = pastedIds.filter((id) =>
                otherGroupsClients.includes(id)
            );

            if (invalidIds.length > 0) {
                showToast(
                    "warning",
                    `Client IDs not found: ${invalidIds.join(", ")}`
                );
            }
            if (alreadyAssignedIds.length > 0) {
                showToast(
                    "warning",
                    `Client IDs already assigned to other groups: ${alreadyAssignedIds.join(
                        ", "
                    )}`
                );
            }
        };

    return (
        <div>
            {privilegesModalDisp ? <PrivilegesModal /> : null}
            {resetPasswordModalVisible ? (
                <ResetPasswordConfirmationModal />
            ) : null}
            {!addUserDisplay ? (
                <>
                    <Row>
                        <Col span={12}>
                            <h5>
                                All Users{" "}
                                <span className="text-muted">
                                    ({paginationData.totalUsers || 0})
                                </span>
                            </h5>
                        </Col>
                        <Col span={12} className="text-end">
                            <Input
                                placeholder="Search by name or email"
                                className="border"
                                style={{ width: "40%" }}
                                defaultValue={searchQuery}
                                onChange={(e) => {
                                    const value = e.target.value.trim();
                                    debouncedSearch(value);
                                }}
                                allowClear
                            />
                            <Button
                                type="primary"
                                className="ms-3"
                                onClick={() => {
                                    setAddUserDisplay((prev) => !prev);
                                }}
                                disabled={!canAddUser}
                            >
                                Add User
                            </Button>
                        </Col>
                    </Row>

                    <div className="users-table-container">
                        <Table
                            rowKey={(record) => record.uid}
                            rowSelection={{
                                type: "checkbox",
                                ...rowSelection,
                            }}
                            dataSource={
                                paginationData.usersList.get(
                                    paginationData.currentPage
                                ) || []
                            }
                            columns={columns}
                            pagination={{
                                current: paginationData.currentPage,
                                pageSize: paginationData.pageSize,
                                total: paginationData.totalUsers,
                                onChange: (page) => {
                                    setPaginationData((prev) => ({
                                        ...prev,
                                        currentPage: page,
                                    }));
                                },
                                onShowSizeChange: (current, size) => {
                                    setPaginationData((prev) => ({
                                        usersList: new Map(),
                                        currentPage: 1,
                                        pageSize: size,
                                        totalUsers: prev.totalUsers,
                                        loading: true,
                                    }));
                                },
                                showSizeChanger: true,
                                pageSizeOptions: DEFAULT_PAGE_SIZE_OPTIONS,
                            }}
                            onRow={(record) => ({
                                onClick: () => handleView(record),
                                className: record.is_archived
                                    ? "archived-row"
                                    : "active-row",
                                style: { cursor: "pointer" },
                            })}
                            loading={paginationData.loading}
                            scroll={{ y: "calc(100vh - 310px)" }}
                        />
                    </div>
                </>
            ) : (
                <>
                    <Row className="">
                        <Col span={12}>
                            <h5>
                                {editUserData ? "Edit User" : "Add User"}
                                {`${cloneUserDisplay ? " (clone)" : ""}`}
                            </h5>
                        </Col>
                        <Col span={12} className="text-end">
                            <Button
                                type="primary"
                                className="ms-3"
                                onClick={() => {
                                    setAddUserDisplay((prev) => !prev);
                                    setEditUserData(null);
                                    setChannels([]);
                                    handleCancelClone();
                                    setUserPrivileges({});
                                }}
                            >
                                Manage Users
                            </Button>
                        </Col>
                    </Row>

                    <div
                        className="mt-4"
                        style={{
                            overflowY: "auto",
                            overflowX: "hidden",
                            height: "75vh",
                        }}
                    >
                        {formLoading ? (
                            <div className="d-flex justify-content-center align-items-center">
                                <Spin />
                            </div>
                        ) : (
                            <Form
                                form={form}
                                layout="vertical"
                                onFinish={handleFormSubmit}
                                initialValues={formInitialValues}
                            >
                                <Row gutter={16}>
                                    <Col span={12}>
                                        <Form.Item
                                            label="First Name"
                                            name="firstName"
                                            rules={usersFormRules.fistName}
                                        >
                                            <Input
                                                className="border"
                                                placeholder="Enter First Name"
                                                disabled={isArchived}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item
                                            label="Last Name"
                                            name="lastName"
                                            rules={usersFormRules.lastName}
                                        >
                                            <Input
                                                className="border"
                                                placeholder="Enter Last Name"
                                                disabled={isArchived}
                                            />
                                        </Form.Item>
                                    </Col>
                                </Row>

                                <Row gutter={16}>
                                    <Col span={12}>
                                        <Form.Item
                                            label="Email"
                                            name="email"
                                            rules={usersFormRules.email}
                                        >
                                            <Input
                                                className="border"
                                                placeholder="Enter Email"
                                                disabled={isArchived}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item
                                            label="Phone"
                                            name="phone"
                                            rules={[
                                                ...usersFormRules.phone,
                                                {
                                                    validator: (_, value) => {
                                                        if (!value) {
                                                            return Promise.resolve();
                                                        }
                                                        if (
                                                            !isValidPhoneNumber(
                                                                `+${value}`
                                                            )
                                                        ) {
                                                            return Promise.reject(
                                                                new Error(
                                                                    "Please enter a valid phone number"
                                                                )
                                                            );
                                                        }
                                                        return Promise.resolve();
                                                    },
                                                },
                                            ]}
                                        >
                                            <PhoneInput
                                                country={"in"}
                                                onChange={(value) =>
                                                    form.setFieldsValue({
                                                        phone: value,
                                                    })
                                                }
                                                inputProps={{
                                                    name: "phone",
                                                    required: false,
                                                }}
                                                inputStyle={{
                                                    marginTop: "8px",
                                                    height: "32px",
                                                    width: "100%",
                                                }}
                                                disabled={isArchived}
                                                enableSearch
                                            />
                                        </Form.Item>
                                    </Col>
                                </Row>

                                <Row gutter={16}>
                                    <Col span={12}>
                                        <Form.Item
                                            label="Country"
                                            name="country"
                                            rules={[
                                                {
                                                    required: true,
                                                    message:
                                                        "Please select your country",
                                                },
                                            ]}
                                        >
                                            <Select
                                                disabled={isArchived}
                                                placeholder="Select Country"
                                                showSearch
                                                filterOption={filterOption}
                                            >
                                                {countriesData.map(
                                                    (country) => (
                                                        <Option
                                                            value={
                                                                country.alpha2_code
                                                            }
                                                            key={
                                                                country.alpha2_code
                                                            }
                                                        >
                                                            {country.name} (
                                                            {
                                                                country.alpha2_code
                                                            }
                                                            )
                                                        </Option>
                                                    )
                                                )}
                                            </Select>
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item
                                            label="Timezone"
                                            name="timezone"
                                            rules={usersFormRules.timezone}
                                        >
                                            <Select
                                                disabled={isArchived}
                                                placeholder="Select Timezone"
                                                showSearch
                                            >
                                                {timezones.map((timezone) => (
                                                    <Option
                                                        value={timezone.label}
                                                        key={timezone.label}
                                                    >
                                                        {timezone.label}
                                                    </Option>
                                                ))}
                                            </Select>
                                        </Form.Item>
                                    </Col>
                                </Row>

                                <Row gutter={16}>
                                    <Col span={12}>
                                        <Form.Item
                                            label="Expiration Date"
                                            name="expiration"
                                            rules={usersFormRules.expiration}
                                        >
                                            <DatePicker
                                                disabled={isArchived}
                                                style={{ width: "100%" }}
                                                placeholder="Select Expiration Date"
                                                format="DD-MM-YYYY"
                                                disabledDate={(current) => {
                                                    return (
                                                        current &&
                                                        (current.isBefore(
                                                            dayjs(),
                                                            "day"
                                                        ) ||
                                                            current.isSame(
                                                                dayjs(),
                                                                "day"
                                                            ))
                                                    );
                                                }}
                                            />
                                        </Form.Item>
                                    </Col>
                                </Row>

                                <Form.List name="access">
                                    {(
                                        clientFields,
                                        { add: addClient, remove: removeClient }
                                    ) => {
                                        const isSuperAdmin =
                                            editUserData?.is_super_admin;

                                        return (
                                            <>
                                                {isSuperAdmin && (
                                                    <div className="bg-info-subtle border border-info rounded p-3 mb-3 text-center">
                                                        <strong>
                                                            Super Admin Mode:
                                                        </strong>{" "}
                                                        Client access
                                                        configuration is
                                                        disabled. Super admins
                                                        have access to all
                                                        clients and platforms
                                                        automatically.
                                                    </div>
                                                )}

                                                {clientFields.map(
                                                    (
                                                        clientField,
                                                        clientIndex
                                                    ) => (
                                                        <div
                                                            key={
                                                                clientField.key
                                                            }
                                                        >
                                                            <Row
                                                                gutter={16}
                                                                className="p-4 rounded mb-4 position-relative"
                                                                style={{
                                                                    background:
                                                                        isSuperAdmin
                                                                            ? "#f5f5f5"
                                                                            : "#f0f2f5",
                                                                    opacity:
                                                                        isSuperAdmin
                                                                            ? 0.6
                                                                            : 1,
                                                                }}
                                                            >
                                                                {/* Client Section */}
                                                                <Col span={24}>
                                                                    <Form.Item
                                                                        label={
                                                                            <Tooltip title="Tip: You can paste comma-separated client IDs (e.g. '1,2,3') to select multiple clients at once">
                                                                                <span>
                                                                                    Client{" "}
                                                                                    <InfoCircleOutlined
                                                                                        style={{
                                                                                            color: "#1677ff",
                                                                                            marginLeft: 4,
                                                                                        }}
                                                                                    />
                                                                                </span>
                                                                            </Tooltip>
                                                                        }
                                                                        name={[
                                                                            clientField.name,
                                                                            "clients",
                                                                        ]}
                                                                        rules={[
                                                                            {
                                                                                required:
                                                                                    true,
                                                                                message:
                                                                                    "Please select your client",
                                                                            },
                                                                        ]}
                                                                    >
                                                                        <div
                                                                            onPaste={handlePaste(
                                                                                clientIndex
                                                                            )}
                                                                        >
                                                                            <Select
                                                                                mode="multiple"
                                                                                disabled={
                                                                                    isArchived ||
                                                                                    isSuperAdmin
                                                                                }
                                                                                placeholder="Select Client(s)"
                                                                                showSearch
                                                                                allowClear
                                                                                value={form.getFieldValue(
                                                                                    [
                                                                                        "access",
                                                                                        clientIndex,
                                                                                        "clients",
                                                                                    ]
                                                                                )}
                                                                                onChange={(
                                                                                    value
                                                                                ) => {
                                                                                    handleClientChange(
                                                                                        value,
                                                                                        clientIndex
                                                                                    );
                                                                                }}
                                                                                filterOption={(
                                                                                    input,
                                                                                    option: CustomOptionType
                                                                                ) => {
                                                                                    const client =
                                                                                        clients.find(
                                                                                            (
                                                                                                c
                                                                                            ) =>
                                                                                                c.id ===
                                                                                                option.value
                                                                                        );
                                                                                    if (
                                                                                        !client
                                                                                    )
                                                                                        return false;

                                                                                    const searchText =
                                                                                        `${
                                                                                            client.id
                                                                                        } ${
                                                                                            client.name
                                                                                        } ${
                                                                                            client.country_non_iso ||
                                                                                            client.country_iso
                                                                                        }`.toLowerCase();
                                                                                    return searchText.includes(
                                                                                        input.toLowerCase()
                                                                                    );
                                                                                }}
                                                                            >
                                                                                {clients?.map(
                                                                                    (
                                                                                        client
                                                                                    ) => {
                                                                                        return (
                                                                                            <Option
                                                                                                key={
                                                                                                    client.id
                                                                                                }
                                                                                                value={
                                                                                                    client.id
                                                                                                }
                                                                                                disabled={checkIsClientOptionDisabled(
                                                                                                    clientIndex,
                                                                                                    client
                                                                                                )}
                                                                                            >
                                                                                                <div className="d-flex justify-content-between align-items-center">
                                                                                                    <div
                                                                                                        className="client-info"
                                                                                                        data-country={
                                                                                                            client.country_non_iso ||
                                                                                                            client.country_iso
                                                                                                        }
                                                                                                        data-currency={
                                                                                                            client.currency
                                                                                                        }
                                                                                                        data-timezone={
                                                                                                            client.timezone
                                                                                                        }
                                                                                                    >
                                                                                                        {`${
                                                                                                            client.id
                                                                                                        } : ${
                                                                                                            client.name
                                                                                                        } (${
                                                                                                            client.country_non_iso ||
                                                                                                            client.country_iso
                                                                                                        })`}
                                                                                                        {editUserData?.default_client ===
                                                                                                            client.id && (
                                                                                                            <Tag className="default-client-tag ms-2">
                                                                                                                Default
                                                                                                            </Tag>
                                                                                                        )}
                                                                                                    </div>
                                                                                                </div>
                                                                                            </Option>
                                                                                        );
                                                                                    }
                                                                                )}
                                                                            </Select>
                                                                        </div>
                                                                    </Form.Item>
                                                                </Col>

                                                                {/* Platforms Section */}
                                                                <Col span={12}>
                                                                    <Form.Item label="Platform Access">
                                                                        <Form.Item
                                                                            {...clientField}
                                                                            name={[
                                                                                clientField.name,
                                                                                "platforms",
                                                                            ]}
                                                                            noStyle
                                                                            rules={
                                                                                usersFormRules.platforms
                                                                            }
                                                                        >
                                                                            <Select
                                                                                mode="multiple"
                                                                                disabled={
                                                                                    isArchived ||
                                                                                    isSuperAdmin
                                                                                }
                                                                                placeholder="Select Platforms"
                                                                                style={{
                                                                                    width: "100%",
                                                                                }}
                                                                            >
                                                                                {platformOptions.map(
                                                                                    (
                                                                                        platform
                                                                                    ) => (
                                                                                        <Option
                                                                                            key={
                                                                                                platform
                                                                                            }
                                                                                            value={
                                                                                                platform
                                                                                            }
                                                                                        >
                                                                                            {
                                                                                                platform
                                                                                            }
                                                                                        </Option>
                                                                                    )
                                                                                )}
                                                                            </Select>
                                                                        </Form.Item>
                                                                    </Form.Item>
                                                                </Col>

                                                                {/* Role Section */}
                                                                <Col
                                                                    span={12}
                                                                    className="d-flex align-items-center"
                                                                >
                                                                    <Form.Item
                                                                        {...clientField}
                                                                        label={
                                                                            <Tooltip title="Please select only one role">
                                                                                <span className="d-flex align-items-center">
                                                                                    Role
                                                                                    <InfoCircleOutlined
                                                                                        className="ms-1"
                                                                                        style={{
                                                                                            color: "#1677ff",
                                                                                        }}
                                                                                    />
                                                                                </span>
                                                                            </Tooltip>
                                                                        }
                                                                        name={[
                                                                            clientField.name,
                                                                            "role_id",
                                                                        ]}
                                                                        style={{
                                                                            width: "100%",
                                                                        }}
                                                                        rules={
                                                                            usersFormRules.role
                                                                        }
                                                                    >
                                                                        <Select
                                                                            disabled={
                                                                                isArchived ||
                                                                                isSuperAdmin
                                                                            }
                                                                            showSearch
                                                                            placeholder="Select Role"
                                                                            className="w-100"
                                                                            filterOption={
                                                                                filterOption
                                                                            }
                                                                            onChange={(
                                                                                value
                                                                            ) =>
                                                                                handleRoleChange(
                                                                                    value,
                                                                                    clientIndex
                                                                                )
                                                                            }
                                                                        >
                                                                            {availableRoles?.map(
                                                                                (
                                                                                    role
                                                                                ) => (
                                                                                    <Option
                                                                                        key={
                                                                                            role.role_id
                                                                                        }
                                                                                        value={
                                                                                            role.role_id
                                                                                        }
                                                                                    >
                                                                                        {
                                                                                            role.role_name
                                                                                        }
                                                                                    </Option>
                                                                                )
                                                                            )}
                                                                        </Select>
                                                                    </Form.Item>

                                                                    <div
                                                                        className={`mt-2 text-end ${
                                                                            !canListPermissions
                                                                                ? "d-none"
                                                                                : ""
                                                                        }`}
                                                                        style={{
                                                                            minWidth:
                                                                                "95px",
                                                                        }}
                                                                    >
                                                                        <span
                                                                            className="text-primary"
                                                                            style={{
                                                                                cursor: isArchived
                                                                                    ? "not-allowed"
                                                                                    : "pointer",
                                                                                opacity:
                                                                                    isArchived
                                                                                        ? 0.5
                                                                                        : 1,
                                                                            }}
                                                                            onClick={() => {
                                                                                if (
                                                                                    isArchived
                                                                                )
                                                                                    return;
                                                                                const clientId =
                                                                                    form.getFieldValue(
                                                                                        [
                                                                                            "access",
                                                                                            clientField.name,
                                                                                            "clients",
                                                                                        ]
                                                                                    );

                                                                                // Collect all roles for this client
                                                                                const clientRole =
                                                                                    form.getFieldValue(
                                                                                        [
                                                                                            "access",
                                                                                            clientField.name,
                                                                                            "role_id",
                                                                                        ]
                                                                                    );

                                                                                if (
                                                                                    !clientRole
                                                                                ) {
                                                                                    showToast(
                                                                                        "warning",
                                                                                        "Please select at least one role first"
                                                                                    );
                                                                                    return;
                                                                                }

                                                                                if (
                                                                                    clientId.length >
                                                                                    0
                                                                                ) {
                                                                                    setPrivilegesModalDisp(
                                                                                        true
                                                                                    );
                                                                                }

                                                                                setSelectedClientId(
                                                                                    clientId
                                                                                );
                                                                                setSelectedRolesMap(
                                                                                    (
                                                                                        prev
                                                                                    ) => ({
                                                                                        ...prev,
                                                                                        [clientId]:
                                                                                            clientRole,
                                                                                    })
                                                                                );

                                                                                const currentClientPrivileges =
                                                                                    form.getFieldValue(
                                                                                        [
                                                                                            "access",
                                                                                            clientField.name,
                                                                                            "privileges",
                                                                                        ]
                                                                                    ) ||
                                                                                    {};
                                                                                if (
                                                                                    clientId.length >
                                                                                    0
                                                                                ) {
                                                                                    setUserPrivileges(
                                                                                        (
                                                                                            prev: IUserPrivilegesState
                                                                                        ) => ({
                                                                                            ...prev,
                                                                                            [clientId.toString()]:
                                                                                                currentClientPrivileges,
                                                                                        })
                                                                                    );
                                                                                }
                                                                            }}
                                                                        >
                                                                            Privileges
                                                                        </span>
                                                                    </div>
                                                                </Col>
                                                                {clientIndex >
                                                                    0 && (
                                                                    <IoIosCloseCircleOutline
                                                                        onClick={() =>
                                                                            removeClient(
                                                                                clientField.name
                                                                            )
                                                                        }
                                                                        className="position-absolute text-danger"
                                                                        style={{
                                                                            top: "10px",
                                                                            right: "20px",
                                                                            fontSize:
                                                                                "20px",
                                                                            cursor: "pointer",
                                                                        }}
                                                                    />
                                                                )}
                                                                {/* Channel selection */}
                                                                <Col span={12}>
                                                                    <Form.Item label="Channels">
                                                                        <Form.Item
                                                                            {...clientFields}
                                                                            name={[
                                                                                clientField.name,
                                                                                "channels",
                                                                            ]}
                                                                            fieldKey={[
                                                                                clientField.fieldKey,
                                                                                "channels",
                                                                            ]}
                                                                            noStyle
                                                                            rules={
                                                                                usersFormRules.channels
                                                                            }
                                                                        >
                                                                            <ChannelSelect
                                                                                channels={
                                                                                    channels[
                                                                                        clientIndex
                                                                                    ] ||
                                                                                    []
                                                                                }
                                                                                loading={
                                                                                    channelsLoading ===
                                                                                    clientIndex
                                                                                }
                                                                                disabled={
                                                                                    isArchived ||
                                                                                    isSuperAdmin
                                                                                }
                                                                                mode="multiple"
                                                                                placeholder="Select Clients to add Channels"
                                                                                onChange={(
                                                                                    value
                                                                                ) =>
                                                                                    handleChannelChange(
                                                                                        value,
                                                                                        clientIndex
                                                                                    )
                                                                                }
                                                                            />
                                                                        </Form.Item>
                                                                    </Form.Item>
                                                                </Col>
                                                            </Row>
                                                        </div>
                                                    )
                                                )}

                                                <Button
                                                    type="dashed"
                                                    className="w-100 my-3"
                                                    onClick={() =>
                                                        addClient({
                                                            clients: [],
                                                            platforms: [],
                                                            role_id:
                                                                DEFAULT_ROLE_ID,
                                                            privileges: [],
                                                        })
                                                    }
                                                    disabled={
                                                        isArchived ||
                                                        isSuperAdmin
                                                    }
                                                >
                                                    Add More Client
                                                </Button>
                                            </>
                                        );
                                    }}
                                </Form.List>

                                <Row gutter={16} align="middle">
                                    <Col span={4}>
                                        <Form.Item label="Status" name="status">
                                            <Switch
                                                disabled={isArchived}
                                                onChange={(checked) =>
                                                    handleUserFieldToggle(
                                                        "status",
                                                        checked
                                                    )
                                                }
                                                checkedChildren={
                                                    <CheckOutlined />
                                                }
                                                unCheckedChildren={
                                                    <CloseOutlined />
                                                }
                                            />
                                        </Form.Item>
                                    </Col>
                                    {currentUserData?.is_super_admin && (
                                        <Col span={4}>
                                            <Form.Item
                                                label="Super Admin"
                                                name="is_super_admin"
                                                tooltip="Super admins have access to all clients and platforms"
                                            >
                                                <Switch
                                                    disabled={isArchived}
                                                    onChange={(checked) => {
                                                        handleUserFieldToggle(
                                                            "is_super_admin",
                                                            checked
                                                        );
                                                    }}
                                                    checkedChildren={
                                                        <CheckOutlined />
                                                    }
                                                    unCheckedChildren={
                                                        <CloseOutlined />
                                                    }
                                                />
                                            </Form.Item>
                                        </Col>
                                    )}
                                    {!editUserData ? (
                                        <Col span={6}>
                                            <Form.Item
                                                className="mt-4"
                                                name="sendWelcomeEmail"
                                                valuePropName="checked"
                                            >
                                                <Checkbox
                                                    checked={form.getFieldValue(
                                                        "sendWelcomeEmail"
                                                    )}
                                                    onChange={(e) =>
                                                        handleWelcomeEmailChange(
                                                            e.target.checked
                                                        )
                                                    }
                                                >
                                                    {"Send Welcome Email"}
                                                </Checkbox>
                                            </Form.Item>
                                        </Col>
                                    ) : (
                                        ""
                                    )}
                                </Row>

                                <Row className="mb-7">
                                    <Col span={24} className="d-flex">
                                        <Form.Item>
                                            <Button
                                                type="primary"
                                                htmlType="submit"
                                                disabled={isArchived}
                                                loading={formSubmissionLoading}
                                            >
                                                {editUserData
                                                    ? `${
                                                          formSubmissionLoading
                                                              ? "Saving..."
                                                              : "Save Changes"
                                                      }`
                                                    : `${
                                                          formSubmissionLoading
                                                              ? "Creating..."
                                                              : "Create User"
                                                      }`}
                                            </Button>
                                        </Form.Item>
                                        {isArchived ? (
                                            <Button
                                                className={`ms-3`}
                                                type="primary"
                                                disabled={
                                                    !isArchived ||
                                                    !canReactivateUser
                                                }
                                                loading={reactivateUserLoading}
                                                onClick={() =>
                                                    handleReactiveUser(
                                                        editUserData.uid
                                                    )
                                                }
                                            >
                                                Re-Activate
                                            </Button>
                                        ) : (
                                            ""
                                        )}

                                        <Button
                                            className="ms-3"
                                            onClick={() => {
                                                setAddUserDisplay(false);
                                                setEditUserData(null);
                                                handleCancelClone();
                                                setUserPrivileges({});
                                            }}
                                        >
                                            Cancel
                                        </Button>
                                    </Col>
                                </Row>
                            </Form>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

export default UserComponent;
